import { NgTemplateOutlet } from '@angular/common'
import { Component, computed, EventEmitter, input, Output } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router'
import { Uuid } from '@ftr/contracts/type/shared'
import {
  AnchorComponent,
  BreadcrumbModule,
  BreadcrumbService,
  ButtonColor,
  ButtonComponent,
  IconComponent,
} from '@ftr/foundation'
import { AppPaths, CourtSystemPaths } from '@ftr/routing-paths'
import { AccountDropdownComponent } from '@ftr/ui-core'
import { featureFlag, LaunchDarklyModule } from '@ftr/ui-feature-flags'
import { RequestCartSidePanelToggleComponent, RequestTypeUrlValue } from '@ftr/ui-request'
import { TopBarSearchComponent } from '@ftr/ui-search'
import { UserState } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { filter, map, tap } from 'rxjs'

@Component({
  selector: 'ftr-top-bar-display',
  templateUrl: './top-bar-display.component.html',
  styleUrls: ['./top-bar-display.component.css'],
  imports: [
    AccountDropdownComponent,
    IconComponent,
    BreadcrumbModule,
    TopBarSearchComponent,
    ButtonComponent,
    NgTemplateOutlet,
    RouterLink,
    LaunchDarklyModule,
    AnchorComponent,
    RequestCartSidePanelToggleComponent,
  ],
})
export class TopBarDisplayComponent {
  @Output() sideBarToggled = new EventEmitter<void>()

  currentCourtSystemId = input<Uuid | undefined>()
  currentRequestType = input<RequestTypeUrlValue | undefined>()
  showingBreadcrumbs = false

  buttonColor: ButtonColor = 'grey'

  protected readonly loginPath = `/${AppPaths.Login}`
  protected readonly signupPath = '/signup'

  protected readonly isLoggedIn = toSignal(this.store.select(UserState.isLoggedIn), { initialValue: false })
  protected readonly isPublicNavigationEnabled = featureFlag('public-navigation')

  protected routeUrlSignal = computed(() => this.router.url)

  ngOnInit(): void {
    this.showingBreadcrumbs = this.breadcrumbService.isShowingBreadcrumbs(this.activatedRoute)
  }

  protected newRouteUrlSignal = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => (event instanceof NavigationEnd ? event.url : '')),
      tap(() => (this.showingBreadcrumbs = this.breadcrumbService.isShowingBreadcrumbs(this.activatedRoute))),
    ),
    { initialValue: '' },
  )

  isSearchingSignal = computed(() => {
    const routeUrl = this.routeUrlSignal()
    const newRouteUrl = this.newRouteUrlSignal()
    const searchUrl = [`/${AppPaths.CourtSystem}`, this.currentCourtSystemId(), CourtSystemPaths.Search].join('/')
    // True when we navigate to the search page or start there
    return newRouteUrl.startsWith(searchUrl) || (routeUrl.startsWith(searchUrl) && !newRouteUrl)
  })

  readonly shouldShowCartButton = computed<boolean>(() => {
    const courtSystemId = this.currentCourtSystemId()
    const requestType = this.currentRequestType()
    const isPublicNavigationEnabled = this.isPublicNavigationEnabled()

    return Boolean(courtSystemId && requestType && isPublicNavigationEnabled)
  })

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly store: Store,
  ) {}

  toggleSideBar(): void {
    this.sideBarToggled.emit()
  }
}
